import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { ICONS } from '@ui/icons/types';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import type { SocialLink, SocialPlatform, SocialWidgetSize } from '../../../templates/widgets/social-widget';
import TabRow from '../controls/tab-row';
import TextField from '../controls/text-field';
import ColorPill from '../controls/color-pill';
import AlignmentControl from '../controls/alignment-control';
import BorderControl from '../controls/border-control';
import BoxModelControl from '../controls/box-model-control';
import SizeSwitchControl from '../controls/size-switch-control';
import NumberField from '../controls/number-field';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import styles from './social-style-panel.module.css';

const DEFAULT_PLATFORMS: SocialPlatform[] = [
  ICONS.FACEBOOK,
  ICONS.LINKEDIN,
  ICONS.TWITTER,
  ICONS.INSTAGRAM,
  ICONS.YOUTUBE,
];

const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  [ICONS.FACEBOOK]: 'Facebook',
  [ICONS.LINKEDIN]: 'Linkedin',
  [ICONS.TWITTER]: 'X',
  [ICONS.INSTAGRAM]: 'Instagram',
  [ICONS.YOUTUBE]: 'Youtube',
  [ICONS.TIKTOK]: 'TikTok',
};

const TABS: { value: 'content' | 'styles'; label: string }[] = [
  { value: 'content', label: 'Content' },
  { value: 'styles', label: 'Styles' },
];

// fills in any platform missing from the stored list (new block, or data saved before a platform
// existed) so the panel always shows all of them, in whatever order was last saved
function withAllPlatforms(links: SocialLink[]): SocialLink[] {
  if (!links.length) return DEFAULT_PLATFORMS.map((platform) => ({ platform, href: '', hidden: false }));
  const present = new Set(links.map((link) => link.platform));
  const missing = DEFAULT_PLATFORMS.filter((platform) => !present.has(platform)).map((platform) => ({
    platform,
    href: '',
    hidden: false,
  }));
  return [...links, ...missing];
}

interface SocialLinkRowProps {
  link: SocialLink;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleHidden: () => void;
  onHrefChange: (href: string) => void;
}

function SocialLinkRow({ link, isExpanded, onToggleExpand, onToggleHidden, onHrefChange }: SocialLinkRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: link.platform,
  });

  const hasHref = link.href !== '';

  return (
    <Container
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex-column gap-4"
    >
      <Container className={classNames('flex ai-center gap-4', styles.row)}>
        <Container
          className={classNames('flex-shrink-0 ai-center jc-center', styles.dragHandle)}
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${PLATFORM_LABELS[link.platform]}`}
        >
          <Icon name={ICONS.DRAG_HANDLE} size={12} color="var(--global-ico-text-3)" />
        </Container>

        <Container
          className="flex ai-center jc-space-between padding-4 radius-10 flex-1"
          background={hasHref ? 'var(--global-cont-3)' : 'var(--global-cont-2)'}
          minWidth="0"
        >
          <Container className="flex ai-center gap-4 flex-1" minWidth="0">
            <Container className="flex ai-center jc-center padding-8 radius-100 flex-shrink-0">
              <Icon name={link.platform} size={16} color="var(--global-ico-text-1)" />
            </Container>
            <Text
              className="L1 text-ellipsis overflow-hidden"
              color={hasHref ? 'var(--global-ico-text-1)' : 'var(--global-ico-text-3)'}
            >
              {PLATFORM_LABELS[link.platform]}
            </Text>
          </Container>

          <Container className="flex ai-center gap-2 flex-shrink-0">
            <Button
              clear
              className={classNames('ai-center jc-center', styles.editButton)}
              width="32px"
              height="32px"
              padding="0"
              borderRadius="100px"
              hoverBackgroundColor="var(--gray-800)"
              onClick={onToggleExpand}
              aria-label={`Edit ${PLATFORM_LABELS[link.platform]} link`}
              title="Edit link"
            >
              <Icon name={ICONS.EDIT} size={12} color="var(--global-ico-text-2)" />
            </Button>
            <Button
              clear
              className="ai-center jc-center"
              width="32px"
              height="32px"
              padding="0"
              borderRadius="100px"
              hoverBackgroundColor="var(--gray-800)"
              onClick={onToggleHidden}
              aria-label={
                link.hidden ? `Show ${PLATFORM_LABELS[link.platform]}` : `Hide ${PLATFORM_LABELS[link.platform]}`
              }
              title={link.hidden ? 'Hidden - click to show' : 'Visible - click to hide'}
            >
              <Icon
                name={link.hidden ? ICONS.VIEWERS_EYE_OFF : ICONS.VIEWERS_EYE}
                size={14}
                color="var(--global-ico-text-2)"
              />
            </Button>
          </Container>
        </Container>
      </Container>

      <SlideDownCard open={isExpanded} autoGrow animateOnInitialLoad={false}>
        <Container padding="0 0 0 28px">
          <TextField
            label={`${PLATFORM_LABELS[link.platform]} URL`}
            placeholder="eg: https://www.facebook.com/yourpage"
            value={link.href}
            onChange={onHrefChange}
          />
        </Container>
      </SlideDownCard>
    </Container>
  );
}

interface SocialStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  onUpdateProp: (key: string, value: unknown) => void;
}

// Implementation of the Figma "Social" sidepane. All 5 platforms are always persisted to
// block.props.links (in their current display order) so reordering has something stable to work
// with; SocialWidget filters out entries with no href or hidden:true when actually rendering.
export default function SocialStylePanel({ block, onUpdateStyle, onUpdateProp }: SocialStylePanelProps) {
  const [activeTab, setActiveTab] = useState<'content' | 'styles'>('content');
  const [expandedPlatform, setExpandedPlatform] = useState<SocialPlatform | null>(null);

  const style = block.style ?? {};
  const size = (style.size as SocialWidgetSize | undefined) ?? 'medium';
  const align = (style.align as string | undefined) ?? 'center';

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [undefined, undefined, undefined, undefined],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  const links = withAllPlatforms((block.props.links as SocialLink[] | undefined) ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateLinks = (next: SocialLink[]) => onUpdateProp('links', next);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = links.findIndex((link) => link.platform === active.id);
    const newIndex = links.findIndex((link) => link.platform === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    updateLinks(arrayMove(links, oldIndex, newIndex));
  };

  const handleToggleHidden = (platform: SocialPlatform) => {
    updateLinks(links.map((link) => (link.platform === platform ? { ...link, hidden: !link.hidden } : link)));
  };

  const handleHrefChange = (platform: SocialPlatform, href: string) => {
    updateLinks(links.map((link) => (link.platform === platform ? { ...link, href } : link)));
  };

  return (
    <Container className="flex-column gap-16">
      <Container padding="0 16px">
        <TabRow tabs={TABS} value={activeTab} onChange={setActiveTab} />
      </Container>

      {activeTab === 'content' ? (
        <Container className="flex-column gap-8" padding="0 16px">
          <Text className="L1" color="var(--global-ico-text-1)">
            Social links
          </Text>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={links.map((link) => link.platform)} strategy={verticalListSortingStrategy}>
              <Container className="flex-column gap-8">
                {links.map((link) => (
                  <SocialLinkRow
                    key={link.platform}
                    link={link}
                    isExpanded={expandedPlatform === link.platform}
                    onToggleExpand={() =>
                      setExpandedPlatform((prev) => (prev === link.platform ? null : link.platform))
                    }
                    onToggleHidden={() => handleToggleHidden(link.platform)}
                    onHrefChange={(href) => handleHrefChange(link.platform, href)}
                  />
                ))}
              </Container>
            </SortableContext>
          </DndContext>
        </Container>
      ) : (
        <Container className="flex-column gap-16" padding="0 16px">
          <SizeSwitchControl value={size} onChange={(value) => onUpdateStyle('size', value)} />

          <Container className="flex gap-16">
            <ColorPill
              label="Icon color"
              value={style.color as string | undefined}
              isOverridden={style.color !== undefined}
              onChange={(value) => onUpdateStyle('color', value)}
              onReset={() => onUpdateStyle('color', undefined)}
            />

            <AlignmentControl value={align} onChange={(value) => onUpdateStyle('align', value)} />
          </Container>

          <ColorPill
            label="Background color"
            value={style.background as string | undefined}
            isOverridden={style.background !== undefined}
            onChange={(value) => onUpdateStyle('background', value)}
            onReset={() => onUpdateStyle('background', undefined)}
          />

          <BorderControl style={style} onUpdateStyle={onUpdateStyle} />

          <BoxModelControl
            sectionLabel="Padding"
            expanded={padding.expanded}
            onToggleExpand={() => padding.setExpanded((prev) => !prev)}
            toggleIcon={ICONS.PADDING_SIDES}
            toggleLabel="padding controls"
            values={padding.values}
            onChangeSlot={padding.setSlot}
            fields={[
              { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
              { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
              { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
              { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
            ]}
            renderCollapsed={() => (
              <Container className="flex gap-12 ai-flex-end">
                <NumberField
                  label="Padding"
                  icon={ICONS.PADDING_VERTICAL}
                  allowNegative={false}
                  min={0}
                  max={100}
                  value={padding.values[0]}
                  onChange={padding.setCollapsedFirst}
                />
                <NumberField
                  icon={ICONS.PADDING_HORIZONTAL}
                  allowNegative={false}
                  min={0}
                  max={100}
                  value={padding.values[1]}
                  onChange={padding.setCollapsedSecond}
                />
              </Container>
            )}
          />
        </Container>
      )}
    </Container>
  );
}
